import { useState, useEffect } from 'react';
import { Select, Spin, Tag, Tabs } from 'antd';

const { Option, OptGroup } = Select;

const NEW_REF_TYPE = 'new-ref';

type OptionType = {
  label: string;
  value: string;
  type: string;
};

type BranchTagData = {
  branches?: string[];
  tags?: string[];
};

type BranchTagSelectProps = {
  value?: string;
  onChange?: (value: string) => void;
  data?: BranchTagData | null;
  isLoading?: boolean;
  error?: unknown;
  placeholder?: string;
  disabled?: boolean;
  style?: React.CSSProperties;
  allowClear?: boolean;
  allowCustomValue?: boolean;
  defaultRef?: string;
};

const BranchTagSelect: React.FC<BranchTagSelectProps> = ({
  value,
  onChange,
  data,
  isLoading = false,
  error,
  placeholder = 'Select a reference',
  disabled = false,
  style = { width: '100%' },
  allowClear = true,
  allowCustomValue = true,
  defaultRef,
}) => {
  const [branches, setBranches] = useState<OptionType[]>([]);
  const [tags, setTags] = useState<OptionType[]>([]);
  const [selectedType, setSelectedType] = useState<'branches' | 'tags'>(
    'branches',
  );
  const [searchText, setSearchText] = useState('');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  useEffect(() => {
    if (error) {
      setBranches([]);
      setTags([]);
    } else if (data) {
      const branchOptions = (data.branches || []).map((branch: string) => ({
        label: branch,
        value: branch,
        type: 'branch',
      }));
      const tagOptions = (data.tags || []).map((tag: string) => ({
        label: tag,
        value: tag,
        type: 'tag',
      }));
      setBranches(branchOptions);
      setTags(tagOptions);
    }
  }, [data, error]);

  const filterOptions = (input: string, option?: OptionType) =>
    option ? option.label.toLowerCase().includes(input.toLowerCase()) : false;

  const handleTabChange = (key: string) => {
    if (key === 'branches' || key === 'tags') {
      setSelectedType(key);
    }
  };

  const getFilteredOptions = () => {
    const options = selectedType === 'branches' ? branches : tags;
    const filtered = options.filter((option) =>
      filterOptions(searchText, option),
    );

    filtered.sort((a, b) => {
      if (defaultRef && a.value === defaultRef) return -1;
      if (defaultRef && b.value === defaultRef) return 1;
      return a.label.localeCompare(b.label);
    });

    if (
      allowCustomValue &&
      searchText &&
      !filtered.some((option) => option.value === searchText)
    ) {
      filtered.unshift({
        label: searchText,
        value: searchText,
        type: NEW_REF_TYPE,
      });
    }
    return filtered;
  };

  const handleKeyDown = (
    event: React.KeyboardEvent<HTMLInputElement | HTMLDivElement>,
  ) => {
    if (event.key === 'Backspace' && !searchText) {
      onChange?.('');
    }
  };

  const tabItems = [
    {
      key: 'branches',
      label: 'Branches',
      children: branches.map((branch) => (
        <Option key={branch.value} value={branch.value}>
          {branch.label}
        </Option>
      )),
    },
    {
      key: 'tags',
      label: 'Tags',
      children: tags.map((tag) => (
        <Option key={tag.value} value={tag.value}>
          {tag.label}
        </Option>
      )),
    },
  ];

  const showFallbackInput =
    error ||
    isLoading ||
    (!isLoading && branches.length === 0 && tags.length === 0);

  if (showFallbackInput) {
    return (
      <Select
        showSearch
        allowClear={allowClear}
        filterOption={false}
        value={value || undefined}
        placeholder={placeholder}
        style={style}
        disabled={disabled}
        notFoundContent={
          isLoading ? (
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                minHeight: 32,
              }}
            >
              <Spin size="small" />
            </div>
          ) : null
        }
        onSearch={(val) => {
          setSearchText(val);
          if (allowCustomValue) {
            onChange?.(val);
          }
        }}
        onChange={(val) => {
          setSearchText(val);
          onChange?.(val);
        }}
        onKeyDown={handleKeyDown}
        options={
          value
            ? [
                {
                  label: (
                    <div>
                      <span>{value}</span>
                    </div>
                  ),
                  value: value,
                },
              ]
            : []
        }
      />
    );
  }

  return (
    <Select
      showSearch
      allowClear={allowClear}
      placeholder={placeholder}
      value={value || undefined}
      disabled={disabled}
      onChange={(val) => {
        onChange?.(val);
        setSearchText('');
      }}
      onSearch={setSearchText}
      onKeyDown={handleKeyDown}
      onDropdownVisibleChange={(open) => {
        setIsDropdownOpen(open);
      }}
      style={style}
      dropdownRender={(menu) => (
        <>
          {isDropdownOpen && isLoading ? (
            <div style={{ textAlign: 'center', padding: 10 }}>
              <Spin size="small" />
            </div>
          ) : (
            <div style={{ paddingLeft: 10 }}>
              <Tabs
                defaultActiveKey="branches"
                items={tabItems}
                onChange={handleTabChange}
              />
              {menu}
            </div>
          )}
        </>
      )}
    >
      <OptGroup label={selectedType === 'branches' ? 'Branches' : 'Tags'}>
        {getFilteredOptions().map((option) => (
          <Option key={option.value} value={option.value}>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <span>{option.label}</span>
              {option.type === NEW_REF_TYPE && (
                <Tag color="green" style={{ marginLeft: 8 }}>
                  New
                </Tag>
              )}
              {defaultRef && option.value === defaultRef && (
                <Tag color="geekblue" style={{ marginLeft: 8 }}>
                  default
                </Tag>
              )}
            </div>
          </Option>
        ))}
      </OptGroup>
    </Select>
  );
};

export default BranchTagSelect;
